import { useEffect, useState } from "react";

const ConfirmModal = ({
    open,
    type = "warning", // 'warning', 'danger', 'info', 'success'
    title = "Confirm Action",
    content, // Can be string or React element
    buttons = "ok-cancel", // Preset: 'ok', 'ok-cancel', 'yes', 'yes-no', 'cancel'
    customButtons, // Array of { label, action: 'confirm'|'cancel'|'custom', key?, onClick? }
    count,
    onConfirm,
    onCancel,
    onClose,
    showCloseButton = true,
    // Legacy props support
    message,
    subMessage,
    confirmText,
    cancelText,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const finalType = (() => {
        return type;
    })();

    const finalContent =
        content || message || "Are you sure you want to proceed?";
    const finalConfirmText = confirmText || "OK";
    const finalCancelText = cancelText || "Cancel";

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !isSubmitting) {
                handleCancel();
            }
            if (e.key === "Enter" && !isSubmitting) {
                handleConfirm();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onConfirm, isSubmitting]);

    const getColorClass = () => {
        switch (finalType) {
            case "danger":
                return "bg-danger";
            case "info":
                return "bg-info";
            case "success":
                return "bg-success";
            case "warning":
            default:
                return "bg-warning";
        }
    };

    const getTextColor = () => {
        switch (finalType) {
            case "danger":
            case "info":
            case "success":
                return "text-white";
            case "warning":
            default:
                return "text-dark";
        }
    };

    const getButtonClass = () => {
        switch (finalType) {
            case "danger":
                return "btn-danger";
            case "info":
                return "btn-info";
            case "success":
                return "btn-success";
            case "warning":
            default:
                return "btn-warning";
        }
    };

    const getCloseButtonClass = () => {
        switch (finalType) {
            case "warning":
                return "btn-close";
            default:
                return "btn-close btn-close-white";
        }
    };

    const handleConfirm = async () => {
        if (isSubmitting) return;
        try {
            setIsSubmitting(true);
            const maybePromise = onConfirm?.();
            if (maybePromise && typeof maybePromise.then === "function") {
                await maybePromise;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (isSubmitting) return;
        onCancel?.();
        onClose?.();
    };

    // Default button presets
    const getDefaultButtons = () => {
        const presets = {
            ok: [{ label: "OK", action: "confirm" }],
            "ok-cancel": [
                { label: finalCancelText, action: "cancel" },
                { label: finalConfirmText, action: "confirm" },
            ],
            yes: [{ label: "Yes", action: "confirm" }],
            "yes-no": [
                { label: "No", action: "cancel" },
                { label: "Yes", action: "confirm" },
            ],
            cancel: [{ label: "Cancel", action: "cancel" }],
        };
        return presets[buttons] || presets["ok-cancel"];
    };

    const finalButtons = customButtons || getDefaultButtons();

    if (!open) {
        return null;
    }

    return (
        <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSubmitting)
                    handleCancel();
            }}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className={`modal-header ${getColorClass()}`}>
                        <h5 className={`modal-title ${getTextColor()} fw-bold`}>
                            {title}
                        </h5>
                        {showCloseButton && (
                            <button
                                type="button"
                                className={getCloseButtonClass()}
                                onClick={() => !isSubmitting && handleCancel()}
                                disabled={isSubmitting}
                                aria-label="Close"
                            ></button>
                        )}
                    </div>

                    <div className="modal-body">
                        {typeof finalContent === "string" ? (
                            <div
                                className="text-dark"
                                style={{ whiteSpace: "pre-wrap" }}
                            >
                                {finalContent}
                            </div>
                        ) : (
                            finalContent
                        )}

                        {subMessage && (
                            <p className="text-muted mt-3 text-pre-wrap">
                                {subMessage}
                            </p>
                        )}

                        {typeof count === "number" && (
                            <p className="mt-4 text-dark fw-bold">
                                Total device selected:&nbsp;
                                <span className="badge bg-info">{`${count}`}</span>
                            </p>
                        )}
                    </div>

                    <div className="modal-footer">
                        {finalButtons.map((btn, idx) => (
                            <button
                                key={btn.key || idx}
                                type="button"
                                className={`btn ${
                                    btn.action === "confirm"
                                        ? getButtonClass()
                                        : "btn-secondary"
                                }`}
                                onClick={() => {
                                    if (isSubmitting) return;
                                    if (btn.action === "confirm") {
                                        handleConfirm();
                                    } else if (btn.action === "cancel") {
                                        handleCancel();
                                    } else if (btn.onClick) {
                                        btn.onClick();
                                    }
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting && btn.action === "confirm" && (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                )}
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
